import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { getDocById } from "../../../utils/firebase/Firebase.utils";
import {
  FiX,
  FiLoader,
  FiMapPin,
  FiClock,
  FiTag,
  FiUser,
  FiHome,
  FiSquare,
  FiPhone,
  FiMail,
} from "react-icons/fi";
import { FaMoneyBillWave } from "react-icons/fa";

function ProductDetailModal({ productContext, onClose }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!productContext) {
        setError("No product context available");
        setLoading(false);
        return;
      }

      try {
        // Determine which collection to fetch from based on context clues
        let collectionName = "products";
        let productId = productContext.productId;

        // If the context has propertyId, it's from properties collection
        if (productContext.propertyId) {
          collectionName = "properties";
          productId = productContext.propertyId;
        }
        // If the context has parkingId, it's from parkingSpaces collection
        else if (productContext.parkingId) {
          collectionName = "parkingSpaces";
          productId = productContext.parkingId;
        }

        if (!productId) {
          setError("Product ID not found in context");
          setLoading(false);
          return;
        }

        const productData = await getDocById(collectionName, productId);

        if (productData) {
          setProduct({ ...productData, type: collectionName });
        } else {
          setError(`Product not found in ${collectionName} collection`);
        }
      } catch (err) {
        setError("Error fetching product details: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProductDetails();
  }, [productContext]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-[#363062]">
            Loading Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-8 flex justify-center items-center">
          <FiLoader className="w-8 h-8 animate-spin text-[#363062]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-red-500">Error</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700">{error}</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-hidden">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-medium text-[#363062]">
            No Product Found
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          <p className="text-gray-700">Could not find product details.</p>
        </div>
      </div>
    );
  }

  // Render appropriate content based on product type
  return (
    <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl overflow-hidden">
      <div className="p-4 border-b flex justify-between items-center">
        <h3 className="text-lg font-medium text-[#363062]">
          {product.type === "products"
            ? "Product Details"
            : product.type === "properties"
            ? "Property Details"
            : "Parking Space Details"}
        </h3>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
          <FiX className="w-5 h-5" />
        </button>
      </div>

      <div className="overflow-y-auto max-h-[70vh]">
        {/* Image gallery */}
        <div className="relative w-full h-64 bg-gray-100">
          {product.productImage || product.images?.length > 0 ? (
            <img
              src={product.productImage || product.images?.[0]}
              alt={product.productTitle || product.propertyTitle || "Product"}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-200">
              <span className="text-gray-400 text-sm">No image available</span>
            </div>
          )}
        </div>

        <div className="p-6">
          {/* Title and price */}
          <h2 className="text-xl font-bold text-[#363062] mb-2">
            {product.productTitle ||
              product.propertyTitle ||
              product.unitNumber ||
              product.title ||
              "Untitled"}
          </h2>

          <div className="flex items-center text-[#8FCD00] font-bold text-xl mb-4">
            <FaMoneyBillWave className="mr-1" />
            <span>
              ₱{product.price || product.monthlyRent || product.rate || "N/A"}
            </span>
            {product.type === "properties" && product.monthlyRent && (
              <span className="text-sm text-gray-500 ml-1">/month</span>
            )}
          </div>

          {/* Description */}
          <div className="mb-6">
            <h3 className="text-md font-medium text-gray-700 mb-2">
              Description
            </h3>
            <p className="text-gray-600 whitespace-pre-line">
              {product.description || "No description available."}
            </p>
          </div>

          {/* Details Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {/* Common Details */}
            {product.categories && (
              <div className="flex items-start gap-2">
                <FiTag className="mt-1 text-[#363062]" />
                <div>
                  <p className="text-sm font-medium text-gray-700">
                    Categories
                  </p>
                  <p className="text-sm text-gray-600">
                    {Array.isArray(product.categories)
                      ? product.categories.join(", ")
                      : product.categories}
                  </p>
                </div>
              </div>
            )}

            {product.status && (
              <div className="flex items-start gap-2">
                <FiClock className="mt-1 text-[#363062]" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Status</p>
                  <p className="text-sm text-gray-600">{product.status}</p>
                </div>
              </div>
            )}

            {product.sellerName && (
              <div className="flex items-start gap-2">
                <FiUser className="mt-1 text-[#363062]" />
                <div>
                  <p className="text-sm font-medium text-gray-700">Seller</p>
                  <p className="text-sm text-gray-600">{product.sellerName}</p>
                </div>
              </div>
            )}

            {/* Property-specific details */}
            {product.type === "properties" && (
              <>
                {product.address && (
                  <div className="flex items-start gap-2">
                    <FiMapPin className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Location
                      </p>
                      <p className="text-sm text-gray-600">{product.address}</p>
                    </div>
                  </div>
                )}

                {product.bedrooms && (
                  <div className="flex items-start gap-2">
                    <FiHome className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Bedrooms
                      </p>
                      <p className="text-sm text-gray-600">
                        {product.bedrooms}
                      </p>
                    </div>
                  </div>
                )}

                {product.bathrooms && (
                  <div className="flex items-start gap-2">
                    <FiHome className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Bathrooms
                      </p>
                      <p className="text-sm text-gray-600">
                        {product.bathrooms}
                      </p>
                    </div>
                  </div>
                )}

                {product.floorArea && (
                  <div className="flex items-start gap-2">
                    <FiSquare className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Floor Area
                      </p>
                      <p className="text-sm text-gray-600">
                        {product.floorArea} sqm
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {/* Parking-specific details */}
            {product.type === "parkingSpaces" && (
              <>
                {product.location && (
                  <div className="flex items-start gap-2">
                    <FiMapPin className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">
                        Location
                      </p>
                      <p className="text-sm text-gray-600">
                        {product.location}
                      </p>
                    </div>
                  </div>
                )}

                {product.parkingType && (
                  <div className="flex items-start gap-2">
                    <FiTag className="mt-1 text-[#363062]" />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Type</p>
                      <p className="text-sm text-gray-600">
                        {product.parkingType}
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Contact Information */}
          {(product.contactPhone || product.contactEmail) && (
            <div className="border-t pt-4 mt-4">
              <h3 className="text-md font-medium text-gray-700 mb-2">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {product.contactPhone && (
                  <div className="flex items-center gap-2">
                    <FiPhone className="text-[#363062]" />
                    <span className="text-gray-600">
                      {product.contactPhone}
                    </span>
                  </div>
                )}
                {product.contactEmail && (
                  <div className="flex items-center gap-2">
                    <FiMail className="text-[#363062]" />
                    <span className="text-gray-600">
                      {product.contactEmail}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
